/**
 * 10.9BQ — LINGUAGEM VISUAL UNIFICADA: biblioteca ÚNICA de materiais do MUNDO.
 *
 * Antes cada módulo (Arena, Sanctuary, RiftGate, portais, altar, correio…) recriava o mesmo
 * MeshStandardMaterial de rocha e a mesma "energia" aditiva com opacidades diferentes
 * (0.12 / 0.15 / 0.35 / 0.5 / 0.55 / 0.7 / 0.8) — empilhadas, saturavam pra branco e nada
 * combinava. Agora TODO material de cenário sai daqui, cacheado por chave (tipo + cor):
 *
 *   stoneMat(color)          rocha/pedra (roughness 1, flatShading)
 *   darkStoneMat()           pedra escura do hub (pedestais, postes, altar)
 *   metalMat(color)          metal (roughness 0.45, metalness 0.65)
 *   woodMat()                madeira
 *   foliageMat()             folhagem/arbusto (branco — cor vem do instanceColor)
 *   flatMat(color)           cor chapada sem luz (fendas, buracos)
 *   platformMat(emissive)    plataforma escura com emissivo fraco (bases dos portais)
 *   energyMat(color, kind)   ENERGIA: MeshBasicMaterial aditivo, depthWrite false.
 *                            Opacidade PADRÃO por kind (um só padrão pro jogo inteiro):
 *                              glow 0.35 · disc 0.5 · ring 0.45 · crystal 0.75
 *   crystalStdMat(color)     cristal emissivo padrão (emissiveIntensity 0.9, roughness 0.3)
 *
 * REGRA IMPORTANTE — materiais compartilhados NÃO podem ser animados por instância. Se um
 * módulo mexe em `opacity`, `color`, `emissive` ou `emissiveIntensity` por frame (pulsos,
 * troca de tema, estado ativo/inativo), ele DEVE usar `.clone()` do material da biblioteca
 * e animar o clone. Mexer no original muda TODOS os meshes do jogo que o usam. Pelo mesmo
 * motivo, nunca chame `.dispose()` num material vindo daqui (só em clones).
 */
import * as THREE from "three";

export type EnergyKind = "glow" | "disc" | "ring" | "crystal";

/** Opacidade padrão de cada tipo de energia — o único lugar onde esses números existem. */
export const ENERGY_OPACITY: Record<EnergyKind, number> = {
  glow: 0.35,
  disc: 0.5,
  ring: 0.45,
  crystal: 0.75,
};

const cache = new Map<string, THREE.Material>();

function cached<T extends THREE.Material>(key: string, make: () => T): T {
  let m = cache.get(key) as T | undefined;
  if (!m) {
    m = make();
    m.name = key; // ajuda a achar no inspector/debug
    cache.set(key, m);
  }
  return m;
}

const hex = (c: number): string => c.toString(16).padStart(6, "0");

/** Rocha/pedra genérica: fosca, facetada. Cor padrão = cinza-azulado da muralha. */
export function stoneMat(color = 0x3c4354): THREE.MeshStandardMaterial {
  return cached(`stone:${hex(color)}`, () => new THREE.MeshStandardMaterial({ color, roughness: 1, metalness: 0, flatShading: true }));
}

/** Pedra ESCURA do hub (pedestal do núcleo, postes dos lampiões, base do altar). */
export function darkStoneMat(): THREE.MeshStandardMaterial {
  return cached("darkstone", () => new THREE.MeshStandardMaterial({ color: 0x2a2542, roughness: 0.85, metalness: 0.15, flatShading: true }));
}

/** Metal: lâminas, telhados, ferragens. */
export function metalMat(color = 0x565274): THREE.MeshStandardMaterial {
  return cached(`metal:${hex(color)}`, () => new THREE.MeshStandardMaterial({ color, roughness: 0.45, metalness: 0.65, flatShading: true }));
}

/** Madeira (correio, troncos caídos). */
export function woodMat(): THREE.MeshStandardMaterial {
  return cached("wood", () => new THREE.MeshStandardMaterial({ color: 0x5a3a1a, roughness: 0.85, metalness: 0.05, flatShading: true }));
}

/** Folhagem/arbusto instanciado (branco: a cor real vem do instanceColor do bioma). */
export function foliageMat(): THREE.MeshStandardMaterial {
  return cached("foliage", () => new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.85, metalness: 0, flatShading: true }));
}

/** Cor chapada, sem luz (fenda do correio, buracos). */
export function flatMat(color: number): THREE.MeshBasicMaterial {
  return cached(`flat:${hex(color)}`, () => new THREE.MeshBasicMaterial({ color }));
}

/** Plataforma escura das estruturas do hub com emissivo fraco na cor da estrutura. */
export function platformMat(emissive: number, emissiveIntensity = 0.18): THREE.MeshStandardMaterial {
  return cached(`platform:${hex(emissive)}:${emissiveIntensity}`, () =>
    new THREE.MeshStandardMaterial({ color: 0x141033, roughness: 0.6, metalness: 0.4, emissive, emissiveIntensity }),
  );
}

/**
 * ENERGIA aditiva (discos de portal, runas de chão, anéis, cristais de luz, rachaduras).
 * `disc`/`ring` são DoubleSide (aparecem por trás/por baixo); `glow`/`crystal` não.
 * Para animar opacidade/cor: `.clone()`.
 */
export function energyMat(color: number, kind: EnergyKind): THREE.MeshBasicMaterial {
  return cached(`energy:${kind}:${hex(color)}`, () =>
    new THREE.MeshBasicMaterial({
      color,
      transparent: true,
      opacity: ENERGY_OPACITY[kind],
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      side: kind === "disc" || kind === "ring" ? THREE.DoubleSide : THREE.FrontSide,
    }),
  );
}

/**
 * Cristal emissivo padrão (MeshStandardMaterial). `emissive` por padrão = a própria cor;
 * `emissiveIntensity` padrão 0.9 — só passe outro valor quando a peça PRECISA ler diferente
 * (o valor entra na chave do cache). Para pulsar: `.clone()`.
 */
export function crystalStdMat(color: number, emissive = color, emissiveIntensity = 0.9): THREE.MeshStandardMaterial {
  return cached(`crystal:${hex(color)}:${hex(emissive)}:${emissiveIntensity}`, () =>
    new THREE.MeshStandardMaterial({ color, emissive, emissiveIntensity, roughness: 0.3, metalness: 0, flatShading: true }),
  );
}
